import { EntityFieldCodeEnum, type CustomFieldValueData } from '@/common/types/fields'
import { EntityFieldInjectionMethods } from '@/common/types/general'

export type BaseCustomerDTO = {
  // basic fields without include or expand
  id: number
  reference?: string
  title?: string
  firstName?: string
  lastName?: string
  nir?: string
  vip?: boolean
  external?: boolean
  companyName?: string
  companyPosition?: string
  fullName?: string
  whatsAppUserId?: string
  whatsAppDisplayName?: string
  whatsAppPhoneNumber?: string
  // include COMMENTS
  comments?: string
  // include BIRTH_DATA
  birthCity?: string
  birthDate?: string
  birthDepartment?: string
  birthName?: string
  // include POSTAL_ADDRESS
  address1?: string
  address2?: string
  address3?: string
  address4?: string
  address5?: string
  city?: string
  zipCode?: string
  country?: string
  // include AUDIT_DATES
  creationDate?: string
  lastModifiedDate?: string
  deleteDate?: string
  deletionJobExecutionDate?: string
  // include AUDIT_USERS
  creator?: string
  updateUser?: string
  deleteUser?: string
  // expand CUSTOM_FIELDS
  customFields?: CustomFieldValueData[]
}

// Bulk fetch V1
export type CustomerDetailsDTOV1 = BaseCustomerDTO & {
  // expand EMAIL_ADDRESSES
  emailAddresses?: string[]
  // expand PHONE_NUMBERS
  phoneNumbers?: string[]
}

// V4 DTO
export type CustomerDTOV4 = BaseCustomerDTO & {
  // include PHONE_NUMBERS
  homePhone?: string
  mobilePhone?: string
  workPhone?: string
  otherPhone?: string
  fax?: string
  // include EMAIL_ADDRESSES
  emailAddress?: string
  emailAddress2?: string
}

export type Customer = (CustomerDTOV4 | CustomerDetailsDTOV1) & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

// V3 (Used for case and customer listing)

export enum CustomerIncludeEnumV3 {
  COMMENTS = 'COMMENTS',
  BIRTH_DATA = 'BIRTH_DATA',
  POSTAL_ADDRESS = 'POSTAL_ADDRESS',
  AUDIT_DATES = 'AUDIT_DATES',
  AUDIT_USERS = 'AUDIT_USERS',
}

export enum CustomerExpandEnumV3 {
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
  PHONE_NUMBERS = 'PHONE_NUMBERS',
  EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
}

type CustomerAvailableIncludeOptionsV3 = keyof typeof CustomerIncludeEnumV3
type CustomerAvailableExpandOptionsV3 = keyof typeof CustomerExpandEnumV3

type CustomerFieldInjectionMethodV3<T> = T extends CustomerAvailableExpandOptionsV3
  ? (typeof EntityFieldInjectionMethods)['EXPAND']
  : (typeof EntityFieldInjectionMethods)['INCLUDE']

type CustomerFieldInjectionOptionV3<T extends CustomerAvailableIncludeOptionsV3 | CustomerAvailableExpandOptionsV3> = {
  scope: T
  method: CustomerFieldInjectionMethodV3<T>
}

/**
 * Map of customer fields added by an include and an expand
 */
export const CustomerFieldInjectionMapV3 = new Map<
  EntityFieldCodeEnum,
  CustomerFieldInjectionOptionV3<CustomerAvailableIncludeOptionsV3> | CustomerFieldInjectionOptionV3<CustomerAvailableExpandOptionsV3>
>([
  [EntityFieldCodeEnum.CUSTOMER_COMMENTS, { scope: CustomerIncludeEnumV3.COMMENTS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHCITY, { scope: CustomerIncludeEnumV3.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDATE, { scope: CustomerIncludeEnumV3.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDEPARTMENT, { scope: CustomerIncludeEnumV3.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHNAME, { scope: CustomerIncludeEnumV3.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS1, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS2, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS3, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS4, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS5, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CITY, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ZIPCODE, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_COUNTRY, { scope: CustomerIncludeEnumV3.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CREATIONDATE, { scope: CustomerIncludeEnumV3.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_LAST_MODIFIED_DATE, { scope: CustomerIncludeEnumV3.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_DELETEDATE, { scope: CustomerIncludeEnumV3.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [
    EntityFieldCodeEnum.CUSTOMER_DELETION_JOB_EXECUTION_DATE,
    { scope: CustomerIncludeEnumV3.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE },
  ],
  [EntityFieldCodeEnum.CUSTOMER_PHONE, { scope: CustomerExpandEnumV3.PHONE_NUMBERS, method: EntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_EMAILS, { scope: CustomerExpandEnumV3.EMAIL_ADDRESSES, method: EntityFieldInjectionMethods.EXPAND }],
])

export type CustomerFieldConfigV3 = {
  includes?: Set<CustomerIncludeEnumV3>
  expands?: Set<CustomerExpandEnumV3>
}

// V4 (Used for customer card)

export enum CustomerIncludeEnumV4 {
  COMMENTS = 'COMMENTS',
  BIRTH_DATA = 'BIRTH_DATA',
  POSTAL_ADDRESS = 'POSTAL_ADDRESS',
  AUDIT_DATES = 'AUDIT_DATES',
  AUDIT_USERS = 'AUDIT_USERS',
  PHONE_NUMBERS = 'PHONE_NUMBERS',
  EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
}

export enum CustomerExpandEnumV4 {
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
}

type CustomerAvailableIncludeOptionsV4 = keyof typeof CustomerIncludeEnumV4
type CustomerAvailableExpandOptionsV4 = keyof typeof CustomerExpandEnumV4

type CustomerFieldInjectionMethodV4<T> = T extends CustomerAvailableExpandOptionsV4
  ? (typeof EntityFieldInjectionMethods)['EXPAND']
  : (typeof EntityFieldInjectionMethods)['INCLUDE']

type CustomerFieldInjectionOptionV4<T extends CustomerAvailableIncludeOptionsV4 | CustomerAvailableExpandOptionsV4> = {
  scope: T
  method: CustomerFieldInjectionMethodV4<T>
}

/**
 * Map of customer fields added by an include and an expand
 */
export const CustomerFieldInjectionMapV4 = new Map<
  EntityFieldCodeEnum,
  CustomerFieldInjectionOptionV4<CustomerAvailableIncludeOptionsV4> | CustomerFieldInjectionOptionV4<CustomerAvailableExpandOptionsV4>
>([
  [EntityFieldCodeEnum.CUSTOMER_COMMENTS, { scope: CustomerIncludeEnumV4.COMMENTS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHCITY, { scope: CustomerIncludeEnumV4.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDATE, { scope: CustomerIncludeEnumV4.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDEPARTMENT, { scope: CustomerIncludeEnumV4.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHNAME, { scope: CustomerIncludeEnumV4.BIRTH_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS1, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS2, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS3, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS4, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS5, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CITY, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ZIPCODE, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_COUNTRY, { scope: CustomerIncludeEnumV4.POSTAL_ADDRESS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CREATIONDATE, { scope: CustomerIncludeEnumV4.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_LAST_MODIFIED_DATE, { scope: CustomerIncludeEnumV4.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_DELETEDATE, { scope: CustomerIncludeEnumV4.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE }],
  [
    EntityFieldCodeEnum.CUSTOMER_DELETION_JOB_EXECUTION_DATE,
    { scope: CustomerIncludeEnumV4.AUDIT_DATES, method: EntityFieldInjectionMethods.INCLUDE },
  ],
  [EntityFieldCodeEnum.CUSTOMER_MOBILEPHONE, { scope: CustomerIncludeEnumV4.PHONE_NUMBERS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_HOMEPHONE, { scope: CustomerIncludeEnumV4.PHONE_NUMBERS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_WORKPHONE, { scope: CustomerIncludeEnumV4.PHONE_NUMBERS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_OTHERPHONE, { scope: CustomerIncludeEnumV4.PHONE_NUMBERS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_FAX, { scope: CustomerIncludeEnumV4.PHONE_NUMBERS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_EMAIL, { scope: CustomerIncludeEnumV4.EMAIL_ADDRESSES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_EMAIL_2, { scope: CustomerIncludeEnumV4.EMAIL_ADDRESSES, method: EntityFieldInjectionMethods.INCLUDE }],
])

export type CustomerFieldConfigV4 = {
  includes?: Set<CustomerIncludeEnumV4>
  expands?: Set<CustomerExpandEnumV4>
}
